<template>
  <div class="min-h-screen p-2 sm:p-4">
    <!-- Header - Glass Effect -->
    <div class="bg-gradient-to-r from-gray-800/70 to-gray-900/70 backdrop-blur-md border border-gray-700/40 rounded-xl p-4 mb-4">
      <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between">
        <div class="flex items-center mb-3 sm:mb-0">
          <h1 class="text-lg sm:text-xl font-bold text-gray-100">فروش آنلاین {{ order.id || '...' }}</h1>
        </div>
        <div class="flex flex-col sm:flex-row gap-3 sm:gap-5 text-sm">
          <div class="text-left">
            <p class="text-gray-400 text-xs">تاریخ ثبت</p>
            <p class="text-gray-200 font-medium">{{ order.date_time || '--/--/----' }}</p>
          </div>
          <div class="text-left">
            <p class="text-gray-400 text-xs">کد رهگیری اکسونی</p>
            <p class="text-blue-300 font-medium dir-ltr">{{ order.exuni_tracking_code || '------' }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Products Table - Compact Glass Design -->
    <div class="bg-gray-800/40 backdrop-blur-sm border border-gray-700/30 rounded-xl overflow-hidden">
      <!-- Desktop Table -->
      <div class="hidden md:block">
        <div class="bg-gradient-to-r from-gray-800/50 to-gray-900/50 border-b border-gray-700/40">
          <div class="grid grid-cols-12 py-3 px-4">
            <div class="col-span-1 text-center"><span class="text-xs text-gray-400">ردیف</span></div>
            <div class="col-span-2 text-center"><span class="text-xs text-gray-400">شناسه کالا</span></div>
            <div class="col-span-1 text-center"><span class="text-xs text-gray-400">تصویر</span></div>
            <div class="col-span-3 "><span class="text-xs text-gray-400">نام کالا</span></div>
            <div class="col-span-2 text-center"><span class="text-xs text-gray-400">تعداد</span></div>
            <div class="col-span-2 text-center"><span class="text-xs text-gray-400">قیمت واحد</span></div>
            <div class="col-span-1 text-center"><span class="text-xs text-gray-400">جمع</span></div>
          </div>
        </div>

        <div v-if="order.items && order.items.length > 0">
          <div v-for="(item, index) in order.items" :key="index"
               class="grid grid-cols-12 items-center py-3 px-4 border-b border-gray-700/20 hover:bg-gray-700/20">
            <!-- ردیف -->
            <div class="col-span-1 text-center">
              <span class="text-sm text-gray-300">{{ index + 1 }}</span>
            </div>

            <!-- شناسه کالا -->
            <div class="col-span-2 text-center">
              <span class="text-xs font-mono text-blue-300">{{ item.product.sixteen_digit_code || '-' }}</span>
            </div>

            <!-- تصویر -->
            <div class="col-span-1 flex justify-center">
              <img v-if="item.product.image"
                   :src="img_source(item.product.image)"
                   class="w-12 h-12 object-cover rounded border border-gray-600">
              <div v-else class="w-12 h-12 bg-gray-700 rounded border border-gray-600 flex items-center justify-center">
                <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                </svg>
              </div>
            </div>

            <!-- نام کالا -->
            <div class="col-span-3 pr-2">
              <div v-if="item.product.variation_of_name" class="mb-1">
                <span class="text-xs text-gray-400 bg-gray-700/50 px-2 py-1 rounded">{{ item.product.variation_of_name }}</span>
              </div>
              <p class="text-sm text-gray-100">{{ item.product.name || '-' }}</p>
            </div>
            <div class="col-span-2 text-center">
              <span class="text-sm font-medium text-gray-200">{{ item.product_quantity | toMoney }}</span>
              <span class="text-xs text-gray-400 mr-1">عدد</span>
            </div>


            <!-- قیمت واحد -->
            <div class="col-span-2 text-center">
              <p class="text-sm text-gray-200 mb-1">{{ item.price | toMoney }}
                <span class="text-xs text-gray-400">تومان</span>
              </p>
              <div v-if="item.product.regular_price != item.price" class="mt-1">
                <span class="text-xs text-gray-500 line-through">{{ item.product.regular_price | toMoney }}</span>
              </div>
            </div>

            <!-- جمع کل -->
            <div class="col-span-1 text-center">
              <span class="text-sm font-medium text-gray-200">{{ (item.price * item.product_quantity) | toMoney }}</span>
              <span class="text-xs text-gray-400">تومان</span>
            </div>
          </div>
        </div>

        <div v-else class="py-8 text-center">
          <p class="text-gray-400">هیچ محصولی در این سفارش یافت نشد</p>
        </div>
      </div>

      <!-- Mobile Cards -->
      <div class="md:hidden">
        <div v-if="order.items && order.items.length > 0">
          <div v-for="(item, index) in order.items" :key="'mob-' + index"
               class="bg-gray-800/30 border-b border-gray-700/30 p-3">
            <div class="flex items-start">
              <!-- تصویر در موبایل -->
              <div class="ml-3">
                <img v-if="item.product.image"
                     :src="img_source(item.product.image)"
                     class="w-16 h-16 object-cover rounded border border-gray-600">
                <div v-else class="w-16 h-16 bg-gray-700 rounded border border-gray-600 flex items-center justify-center">
                  <svg class="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                </div>
              </div>

              <!-- اطلاعات محصول -->
              <div class="flex-1">
                <!-- ردیف و شناسه -->
                <div class="flex justify-between items-start mb-2">
                  <div class="flex items-center">
                    <span class="text-sm text-gray-400 ml-2">#{{ index + 1 }}</span>
                    <span class="text-xs text-blue-300 bg-gray-700/50 px-2 py-1 rounded">{{ item.product.sixteen_digit_code |toMoney(0) }}</span>
                  </div>

                  <!-- تعداد -->
                  <div class="text-left">
                    <span class="text-sm font-medium text-gray-200">{{ item.product_quantity | toMoney }}</span>
                    <span class="text-xs text-gray-400">عدد</span>
                  </div>
                </div>

                <!-- نام محصول -->
                <div class="mb-2">
                  <div v-if="item.product.variation_of_name">
                    <span class="text-xs text-gray-400">{{ item.product.variation_of_name }}</span>
                  </div>
                  <p class="text-sm font-medium text-gray-100">{{ item.product.name || '-' }}</p>
                </div>

                <!-- قیمت‌ها -->
                <div class="flex justify-between items-center">
                  <div>
                    <p class="text-sm text-gray-200">{{ item.price | toMoney }}
                      <span class="text-xs text-gray-400">تومان</span>
                    </p>
                    <div v-if="item.product.regular_price != item.price">
                      <span class="text-xs text-gray-500 line-through">{{ item.product.regular_price | toMoney }}</span>
                    </div>
                  </div>

                  <div class="text-left">
                    <p class="text-sm font-medium text-gray-100">{{ (item.price * item.product_quantity) | toMoney }}</p>
                    <p class="text-xs text-gray-400">جمع کل</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="py-8 text-center">
          <p class="text-gray-400">هیچ محصولی در این سفارش یافت نشد</p>
        </div>
      </div>

      <!-- جمع کل سفارش -->
      <div class="bg-gradient-to-r from-gray-800/50 to-gray-900/50 border-t border-gray-700/40 p-4">
        <div class="flex justify-between items-center">
          <div class="text-right">
          </div>

          <p class="text-sm text-gray-400">جمع کل سفارش</p>
          <p class="text-lg font-bold text-gray-100">
            {{ order.total_price| toMoney }}
            <span class="text-sm text-gray-400">تومان</span>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PartnerShopOrderDetail',
  props: {
    id: {},
  },
  data() {
    return {
      order: {},
    }
  },
  methods: {
    fetchOrder() {
      this.request({
        url: this.endpoint(`partner-stock/adminOrderDetailByPartner/${this.id}`),
        method: "get",
        success: data => {
          this.order = data
        }
      })
    },

  },
  mounted() {
    this.fetchOrder();
  },
  filters: {
    toMoney(value) {
      if (!value) return '۰';
      return new Intl.NumberFormat('fa-IR').format(value);
    }
  }
}
</script>

<style scoped>
/* تنظیمات RTL */
.dir-ltr {
  direction: ltr;
}

/* اسکرول بار زیبا برای موبایل */
::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}

::-webkit-scrollbar-track {
  background: rgba(30, 41, 59, 0.3);
}

::-webkit-scrollbar-thumb {
  background: #4b5563;
  border-radius: 2px;
}

/* برای فضای بهتر در موبایل */
@media (max-width: 768px) {
  .min-h-screen {
    min-height: 100vh;
    padding-bottom: 20px;
  }
}

/* خط قیمت روی خط خورده */
.line-through {
  text-decoration-line: line-through;
}
</style>